import React, { MouseEvent, createContext, memo, useContext } from 'react';
import { EdgeProps, getBezierPath } from 'reactflow';

import { useDroppable } from '@dnd-kit/core';
import styled from 'styled-components';

import { BackgroundOverlay } from '@components/background-overlay';
import { IconButton } from '@components/icon-button';
import { IconAddBranch } from '@components/icons/add-branch';
import { IconAddSmall } from '@components/icons/add-small';
import { useIsSidebarActive } from '@components/sidebar';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { JourneyActionMoves } from 'api/journey-actions/requests';
import { JourneyType } from 'api/types';
import { TestIdContext } from 'ui/authenticated/main/helpers';

import { showCreateActionSidebar } from './create-action/create-action-sidebar';
import { getSmoothStepPath } from './custom-smooth-step-edge';
import { useJourneyBuilderActionsContext } from './journey-builder-actions-context';
import { setContinueRegistry } from './unsaved-changes-prompt';

const edgeActionContainerSize = 40;
const edgeActionSize = pxToRem(24);

const EdgeActionContainer = styled.div({
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
});

const innerShadow = `0 0 0 ${pxToRem(2)} ${theme.color.elementBlue100}`;
const outerShadowBlue = `0 0 0 ${pxToRem(8)} ${theme.color.blueTrans20}`;
const outerShadowPurple = `0 0 0 ${pxToRem(8)} ${theme.color.uiTrans10}`;
const outerShadowTeal = `0 0 0 ${pxToRem(8)} ${theme.color.tealTrans10}`;

type IsActiveProp = { $isActive: boolean };
type IsBranchProp = { $isBranch: boolean };

const StyledIconButton = styled(IconButton)<IsActiveProp & IsBranchProp>(
  ({ $isActive, $isBranch }) => ({
    borderWidth: pxToRem(1),
    borderStyle: 'solid',
    borderColor: theme.color.border20,
    ':hover, :active': {
      boxShadow: $isBranch ? outerShadowTeal : outerShadowPurple,
      backgroundColor: $isBranch ? theme.color.elementTeal200 : 'none',
      ...($isBranch && {
        '> *': {
          color: 'white',
        },
      }),
    },
    ...($isActive && {
      boxShadow: `${outerShadowBlue} !important`,
    }),
  })
);

type AddButtonProps = IsActiveProp & {
  onClick: (event: MouseEvent<HTMLButtonElement>) => any;
  isBranch: boolean;
};

const AddButton = ({ onClick, $isActive, isBranch, ...rest }: AddButtonProps) => (
  <StyledIconButton
    size="sm"
    variant={$isActive && !isBranch ? 'blue-white' : 'white-purple'}
    icon={isBranch ? <IconAddBranch /> : <IconAddSmall />}
    onClick={onClick}
    $isActive={$isActive}
    $isBranch={isBranch}
    {...rest}
  />
);

type EdgeButtonContainerProps = Pick<EdgeProps, 'targetX' | 'targetY' | 'target'>;

const EdgeButtonContainer: React.FC<EdgeButtonContainerProps> = ({
  targetX,
  targetY,
  target,
  ...rest
}) => (
  <foreignObject
    width={edgeActionContainerSize}
    height={edgeActionContainerSize}
    x={targetX - edgeActionContainerSize / 2}
    y={targetY - (target.endsWith('hidden') ? 12 : edgeActionContainerSize - 2)}
    className="edgebutton-foreignobject"
    requiredExtensions="http://www.w3.org/1999/xhtml"
    onContextMenu={(e) => e.preventDefault()}
    {...rest}
  ></foreignObject>
);

const DroppableSection = styled.div<IsActiveProp>(({ $isActive }) => ({
  position: 'relative',
  width: edgeActionSize,
  height: edgeActionSize,
  backgroundColor: theme.color.backgroundSecondary,
  borderRadius: pxToRem(4),
  borderWidth: pxToRem(2),
  ...($isActive
    ? {
        borderStyle: 'solid',
        borderColor: theme.color.blueTrans20,
        boxShadow: `${innerShadow}, ${outerShadowBlue}`,
      }
    : {
        borderStyle: 'dashed',
        borderColor: theme.color.purple40,
      }),
}));

const DisabledSection = styled.div({
  position: 'relative',
  width: edgeActionSize,
  height: edgeActionSize,
  backgroundColor: theme.color.uiTrans20,
  borderRadius: pxToRem(4),
});

export const MoveContext = createContext({
  availableMoves: {} as JourneyActionMoves,
  isMoveDisabled: false,
});

export const hiddenIdentifier = '0-hidden';
export const hiddenKey = '_empty';

type JourneyEdgeProps = EdgeProps & { journeyId: JourneyType['shortId'] };

type EdgeButtonProps = {
  id: string;
  source: string;
  target: string;
  targetType: string | undefined;
  isBranchButton: boolean;
  isActive: boolean;
  journeyId: JourneyType['shortId'];
};

const EdgeButton = memo(
  ({
    id,
    source,
    target,
    targetType,
    isBranchButton,
    isActive,
    journeyId,
    ...rest
  }: EdgeButtonProps) => {
    const { handleCreate } = useJourneyBuilderActionsContext();
    const { availableMoves, isMoveDisabled } = useContext(MoveContext);
    const droppable = useDroppable({ id, disabled: isMoveDisabled });

    if (targetType === 'branch_base') {
      return null;
    }

    const targetKey = target.endsWith(hiddenIdentifier) ? hiddenKey : target;
    const showSidebar = () => showCreateActionSidebar({ edge: { id, source, target }, journeyId });

    const handleClick = () => {
      if (isBranchButton) {
        handleCreate({
          journey: journeyId,
          parentAction: source,
          childActions: [],
          action: 'branch_base',
        });
      } else {
        setContinueRegistry({ onContinue: showSidebar, id });
        showSidebar();
      }
    };

    if (isMoveDisabled || !droppable.active) {
      return (
        <AddButton onClick={handleClick} $isActive={isActive} isBranch={isBranchButton} {...rest} />
      );
    }
    if (availableMoves[droppable.active.id]?.[source]?.[targetKey]) {
      return (
        <DroppableSection $isActive={droppable.isOver} ref={droppable.setNodeRef} {...rest}>
          <BackgroundOverlay />
        </DroppableSection>
      );
    }
    return (
      <DisabledSection {...rest}>
        <BackgroundOverlay />
      </DisabledSection>
    );
  }
);

export const JourneyEdge = memo(
  ({
    id,
    source,
    sourceX,
    sourceY,
    target,
    targetX,
    targetY,
    markerEnd,
    journeyId,
  }: JourneyEdgeProps) => {
    const descriptiveMap = useContext(TestIdContext);
    const isActive = useIsSidebarActive(id);
    const sourceType = descriptiveMap[source]?.split('-')[0];
    const targetType = descriptiveMap[target]?.split('-')[0];
    const isBranchButton = targetType === undefined && sourceType === 'journey_branch';
    const testId = id
      .replace(source, descriptiveMap[source])
      .replace(target, descriptiveMap[target]);

    const [edgePath] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      borderRadius: 16,
    });

    const containerProps = isBranchButton
      ? { targetX: sourceX, targetY: sourceY + 11 }
      : { targetX, targetY };

    return (
      <>
        {!isBranchButton && (
          <StyledPath
            id={id}
            d={edgePath}
            markerEnd={markerEnd}
            onContextMenu={(e) => e.preventDefault()}
          />
        )}
        <EdgeButtonContainer target={target} {...containerProps}>
          <EdgeActionContainer>
            <EdgeButton
              id={id}
              source={source}
              target={target}
              targetType={targetType}
              isBranchButton={isBranchButton}
              isActive={isActive}
              journeyId={journeyId}
              data-testid={testId}
            />
          </EdgeActionContainer>
        </EdgeButtonContainer>
      </>
    );
  }
);

const StyledPath = styled.path.attrs({ className: 'react-flow__edge-path' })({
  strokeWidth: pxToRem(4),
  stroke: `${theme.color.purple20} !important`,
});

export const LoopEdge = ({ id, sourceX, sourceY, targetX, targetY, markerEnd }: EdgeProps) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    centerX: targetX,
    borderRadius: 16,
  });

  return <StyledLoopPath id={id} d={edgePath} markerEnd={markerEnd} />;
};

const StyledLoopPath = styled.path.attrs({ className: 'react-flow__edge-path' })({
  strokeWidth: pxToRem(4),
  strokeDasharray: '8 8',
  stroke: `${theme.color.purple20} !important`,
});

export const JourneyExtraEdge = (props: EdgeProps) => {
  const { id, sourceX, sourceY, target, targetX, targetY, markerEnd } = props;
  const isTargetActive = useIsSidebarActive(target);
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  return (
    <StyledExtraPath id={id} d={edgePath} markerEnd={markerEnd} $isTargetActive={isTargetActive} />
  );
};

const StyledExtraPath = styled.path.attrs({ className: 'react-flow__edge-path' })<{
  $isTargetActive: boolean;
}>(({ $isTargetActive }) => ({
  strokeWidth: pxToRem(1),
  stroke: `${theme.color.orange100} !important`,
  display: $isTargetActive ? 'block' : 'none',
}));
